'use client';

/**
 * Wallet "Creator Rewards" line (gap B1).
 *
 * Small self-contained card that shows the payable rewards balance, plus a
 * status chip while a withdrawal request is open. The amount carries NO unit
 * word: until 2026-09-30 it read "<n> credits", and Creator Rewards are not
 * credits (CLAUDE.md credits rule 2: Service Credits pay for your own use and
 * can never be withdrawn; Creator Rewards are a separate object, earned from
 * usage, and the only thing that may ever be withdrawn). The header says
 * "Rewards" and the number stands under it. Mounted by
 * BalanceOverview and BalanceCard so both wallet surfaces stay in sync; renders
 * nothing until the summary loads (and stays hidden on error, so a wallet page
 * never breaks on the rewards read).
 *
 * The summary comes from the shared useEarningsSummary() hook, which
 * de-duplicates the /api/earnings/summary call across every mount on the page
 * (this card is mounted from two places, and the analytics Earnings section
 * reads the same payload).
 *
 * Hidden for users who neither earned anything nor published anything: the
 * same `hasEarningsRows || hasPublishedApps` gate the analytics Earnings
 * section uses, so a plain consumer is not shown a zero rewards card.
 *
 * Copy rules: plain language, never implies guaranteed earnings. Cash-out
 * shows a DATE once one is set (`rewards.cashoutDate`); until then the action
 * is present but disabled, with a "Coming soon" tooltip (2026-08-09 product
 * decision — the earlier rule was "to be announced, never soon"). The button
 * is never enabled by this component: `cashout.open` + the payout rails gate
 * the real flow.
 */

import { Coins } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { formatAmount, toDollars } from '@/lib/balance/units';
import { useEarningsSummary } from '@/components/wallet/use-earnings-summary';
import { Tip } from '@/components/ui/tooltip';

export function nanocreditsToCredits(nanocredits: string, basisCreditsPerUsd: number): number {
  return toDollars(BigInt(nanocredits)) * basisCreditsPerUsd;
}

const PENDING_LABEL: Record<string, string> = {
  requested: 'Withdrawal requested',
  in_review: 'Withdrawal in review',
};

/**
 * `variant`:
 *   'card' — the historical standalone card (BalanceCard and other mounts).
 *   'tile' — one column of the wallet overview's stat-tile row (2026-08-09
 *            option-B layout): same data, header label sized to match the
 *            Plan/Purchased tiles and the action pinned to the bottom edge so
 *            the three tiles hold one height.
 */
export function CreatorRewardsCard({ variant = 'card' }: { variant?: 'card' | 'tile' } = {}) {
  const t = useT();
  const { summary } = useEarningsSummary();

  if (!summary) return null;
  // Nothing earned and nothing published: no rewards card at all.
  if (!summary.hasEarningsRows && !summary.hasPublishedApps) return null;

  const rewards = nanocreditsToCredits(summary.availableNanocents, summary.basisCreditsPerUsd);
  const pendingLabel = summary.pendingRequest
    ? PENDING_LABEL[summary.pendingRequest.status] ?? 'Withdrawal pending'
    : null;
  const cashoutLabel = summary.cashoutDate
    ? new Date(summary.cashoutDate).toLocaleDateString()
    : 'to be announced';

  const tile = variant === 'tile';
  return (
    <div className={tile ? 'card p-5 flex flex-col' : 'card p-4'}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Coins className="h-4 w-4" />
          <span className={tile ? 'text-xs font-medium' : 'text-sm font-medium'}>Rewards</span>
        </div>
        {pendingLabel && (
          <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600 ring-1 ring-amber-500/30 dark:text-amber-400">
            {pendingLabel}
          </span>
        )}
      </div>
      {/* No "credits" after the number: rewards are not credits (file-top block). */}
      <p className="text-2xl font-bold">{formatAmount(rewards)}</p>
      {/* Tile diet: the explainer lives in the card variant and the
          cash-out flow; the tile keeps number + action only. */}
      {!tile && (
        <p className="text-xs text-muted-foreground mt-1">
          {t('rewards.accrualNote')}
        </p>
      )}
      <div className={tile ? 'mt-auto pt-3' : 'mt-3'}>
        {summary.cashoutDate ? (
          <p className="text-xs text-muted-foreground">Cash-out opens {cashoutLabel}.</p>
        ) : (
          <Tip label={t('settings.comingSoon')}>
            {/* A disabled <button> swallows pointer events, so the tooltip
                trigger needs a wrapping element to hover. */}
            <span className="inline-block">
              <button type="button" disabled className="btn-secondary text-sm opacity-60 cursor-not-allowed">
                {t('rewards.cashOut')}
              </button>
            </span>
          </Tip>
        )}
      </div>
    </div>
  );
}
